import React, { useEffect, useRef, useState } from 'react';
import { Button, Spin, Empty } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { get_bpr_report_json } from '@/utils/api';
import { REPORT_STYLE, dash, num, dt, printReport } from './reportStyle';

/**
 * Production Record (BPR) — data-driven. Fetches get_bpr_report_json and renders
 * the report (header, sign-off, per-operation details) from JSON in the backend
 * report design. JSON shape: { header, signoff, operations[] }.
 */
const ProductionRecordReport = ({ batchNumber }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    if (!batchNumber) return;
    let active = true;
    setLoading(true); setError(''); setData(null);
    get_bpr_report_json(
      { batch_number: batchNumber },
      (res) => {
        if (!active) return;
        if (res?.data?.status === 'success' && res?.data?.data) setData(res.data.data);
        else setError(res?.data?.message || 'No report data available');
        setLoading(false);
      },
      () => { if (active) { setError('Failed to load report'); setLoading(false); } }
    );
    return () => { active = false; };
  }, [batchNumber]);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spin size="large" tip="Generating report…" /></div>;
  if (error || !data) return <Empty description={error || 'No report'} style={{ padding: 60 }} />;

  const { header = {}, signoff = {}, operations = [] } = data;
  const sg = (o) => (o ? `${dash(o.name)}  •  ${dt(o.date)}` : '—');

  return (
    <div>
      <style>{REPORT_STYLE}</style>
      <div className="no-print" style={{ textAlign: 'right', marginBottom: 8 }}>
        <Button type="primary" icon={<PrinterOutlined />} onClick={() => printReport(rootRef.current, header.batch_number)}>
          Print / Download
        </Button>
      </div>

      <div ref={rootRef} className="bmr-report">
        <div className="container">
          <header>
            <h1>{dash(header.company_name)}</h1>
            <p style={{ fontSize: 18, fontWeight: 'bold', color: '#1a73e8' }}>{dash(header.report_title)}</p>
          </header>

          {/* Header meta */}
          <table>
            <tbody>
              <tr><td><b>Document No:</b> {dash(header.document_no)}</td><td><b>Revision No:</b> {dash(header.revision_no)}</td><td><b>Batch No:</b> {dash(header.batch_number)}</td></tr>
              <tr><td><b>Product Code:</b> {dash(header.product_code)}</td><td><b>Batch Size:</b> {dash(header.batch_size)}</td><td><b>Effective Date:</b> {dt(header.effective_date)}</td></tr>
            </tbody>
          </table>

          {/* Sign-off */}
          <div className="section-title"><table><tbody><tr><th className="header-class">Sign-off</th></tr></tbody></table></div>
          <table>
            <thead><tr className="header-class-1"><th>Prepared By (PD)</th><th>Reviewed By (PD)</th><th>Reviewed By (QA)</th><th>Approved By (QA)</th></tr></thead>
            <tbody>
              <tr>
                <td className="table_data">{sg(signoff.prepared_by_pd)}</td>
                <td className="table_data">{sg(signoff.reviewed_by_pd)}</td>
                <td className="table_data">{sg(signoff.reviewed_by_qa)}</td>
                <td className="table_data">{sg(signoff.approved_by_qa)}</td>
              </tr>
            </tbody>
          </table>

          {/* Operations */}
          {operations.map((op, i) => (
            <div key={i} className="card">
              <h2>Op {dash(op.operation_number)}: {dash(op.operation_name)} ({dash(op.operation_type)})</h2>
              <table>
                <tbody>
                  <tr><td><b>Equipment:</b> {dash(op.equipment_id)}</td><td><b>Date:</b> {dt(op.execution_date)}</td><td><b>Time:</b> {dt(op.time_from)} – {dt(op.time_to)}</td></tr>
                  <tr><td><b>Performed By:</b> {dash(op.performed_by)}</td><td colSpan={2}><b>Checked By:</b> {dash(op.checked_by)}</td></tr>
                </tbody>
              </table>

              {(op.ingredients || []).length > 0 && (
                <>
                  <div className="section-title"><table><tbody><tr><th className="header-class-1">Materials</th></tr></tbody></table></div>
                  <table>
                    <thead><tr className="header-class-1"><th>Material</th><th>Unit</th><th>Standard</th><th>Actual</th><th>In-house Batch No.</th></tr></thead>
                    <tbody>
                      {op.ingredients.map((m, mi) => (
                        <tr key={mi}>
                          <td>{dash(m.material_name)}</td>
                          <td className="table_data">{dash(m.unit)}</td>
                          <td className="table_data">{num(m.standard_quantity, 3)}</td>
                          <td className="table_data">{num(m.actual_quantity, 3)}</td>
                          <td className="table_data">{dash(m.in_house_batch_no)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {(op.parameters || []).length > 0 && (
                <table>
                  <thead><tr className="header-class-1"><th>Parameter</th><th>Value</th><th>Unit</th></tr></thead>
                  <tbody>
                    {op.parameters.map((p, pi) => (
                      <tr key={pi}><td>{dash(p.parameter_name)}</td><td className="table_data">{dash(p.value)}</td><td className="table_data">{dash(p.unit)}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}

              {(op.action_items || []).length > 0 && (
                <table>
                  <thead><tr className="header-class-1"><th>Visual Check</th><th>Value</th></tr></thead>
                  <tbody>
                    {op.action_items.map((a, ai) => (
                      <tr key={ai}><td>{dash(a.label)}</td><td className="table_data">{dash(a.actual_value)}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}

              {op.product && (op.product.wet_weight || op.product.no_of_containers) && (
                <table>
                  <tbody>
                    <tr><td><b>Wet Weight:</b> {dash(op.product.wet_weight)}</td><td><b>No. of Containers:</b> {dash(op.product.no_of_containers)}</td></tr>
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductionRecordReport;
